import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import { MenuItem } from "../../../../../../redux/menuSlice";
import { useLazyGetCartsForRestaurantByRestaurantIdQuery } from "../../../../../../redux/services/restaurantServices";
import CustomItemSetupModal from "../../../../../Orders/CreateOrder/CollectItemsStep/CustomItemSetupModal";
import "./index.css";
import { useLazyGetMenuItemsListQuery } from "../../../../../../redux/services/MenusService";
import { useLazyGetMenuItemsByRestaurantIdQuery } from "../../../../../../redux/services/menuItemService";

// ─── Types ────────────────────────────────────────────────────────────────────
export interface BulkProduct {
  itemId: string;
  displayName: string;
  skuId: string;
  tier: string;
  price: number;
  itemType: string;
  tinyId?: string;
  productId?: string;
  _rawItem?: any;
}

export interface BulkCart {
  cartId: string;
  displayName: string;
  supportedChannels: string[];
  cartType: string;
  cartState: string;
}

export type BulkGridData = Record<string, Record<string, number>>;
export type BulkCustomData = Record<string, { customItems: any[]; customItemTotal: number }>;

interface BulkCartAllocationGridProps {
  restaurantId?: string;
  gridData: BulkGridData;
  customData: BulkCustomData;
  onGridChange: (data: BulkGridData) => void;
  onCustomChange: (data: BulkCustomData) => void;
  onProductsLoaded?: (products: BulkProduct[]) => void;
  onCartsLoaded?: (carts: BulkCart[]) => void;
}

// ─── Tier helpers ─────────────────────────────────────────────────────────────
const TIER_COLORS: Record<string, { bg: string; color: string; tag: string }> = {
  Platinum: { bg: "#F1F5F9", color: "#64748B", tag: "#64748B" },
  Gold:     { bg: "#FFFBEB", color: "#B45309", tag: "#B45309" },
  Silver:   { bg: "#F8FAFC", color: "#475569", tag: "#475569" },
  Bronze:   { bg: "#FFF7ED", color: "#9A3412", tag: "#9A3412" },
  Value:    { bg: "#F0FDF4", color: "#166534", tag: "#166534" },
  Staff:    { bg: "#EFF6FF", color: "#1D4ED8", tag: "#1D4ED8" },
};

const getTierStyle = (tier: string) =>
  TIER_COLORS[tier] || { bg: "#F8FAFC", color: "#374151", tag: "#374151" };

const getTierIcon = (tier: string) => {
  switch (tier?.toLowerCase()) {
    case "platinum": return "💎";
    case "gold":     return "🥇";
    case "silver":   return "🥈";
    case "bronze":   return "🥉";
    default:         return "";
  }
};

const getItemTier = (item: any): string => {
  if (item.tags?.class && Array.isArray(item.tags.class) && item.tags.class.length > 0) {
    const v = item.tags.class[0];
    if (v) {
      const n = v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
      if (["Platinum", "Gold", "Silver", "Bronze"].includes(n)) return n;
    }
  }
  return item.tier || item.class || "No tier";
};

// ─── Rice helper ──────────────────────────────────────────────────────────────
// Looks at the display name for the words "yellow" or "white"
const getRiceType = (product: BulkProduct): "Yellow" | "White" | "Other" => {
  const name = (product.displayName || "").toLowerCase();
  if (name.includes("yellow")) return "Yellow";
  if (name.includes("white"))  return "White";
  return "Other";
};

// ─── Cuisine helper ───────────────────────────────────────────────────────────
// Detects cuisine(s) purely from the display name
const getCuisineTypes = (product: BulkProduct): string[] => {
  const name = (product.displayName || "").toLowerCase();
  const types: string[] = [];

  if (name.includes("chicken"))                                                    types.push("Chicken");
  if (
    name.includes("fish")       ||
    name.includes("tuna")       ||
    name.includes("modha")      ||
    name.includes("parawa")     ||
    name.includes("thalapath")  ||
    name.includes("ambulthiyal")||
    name.includes("ambul thiyal")
  )                                                                                types.push("Fish");
  if (name.includes("egg") || name.includes("omelette") || name.includes("omelet")) types.push("Egg");
  if (
    name.includes("veg")       ||
    name.includes("soya")      ||
    name.includes("mushroom")  ||
    name.includes("dhal")
  )                                                                                types.push("Veg");
  if (name.includes("meatball"))                                                   types.push("Meatballs");

  return types.length > 0 ? types : ["Other"];
};

const CARTS_PER_PAGE = 10;

// ─── Filter options ───────────────────────────────────────────────────────────
const TIER_FILTERS    = ["All Tiers", "Platinum", "Gold", "Bronze"];
const RICE_FILTERS    = ["All", "White", "Yellow"];
const CUISINE_FILTERS = ["All", "Chicken", "Fish", "Egg", "Veg", "Meatballs"];

// ─── Component ────────────────────────────────────────────────────────────────
export const BulkCartAllocationGrid: React.FC<BulkCartAllocationGridProps> = ({
  restaurantId,
  gridData,
  customData,
  onGridChange,
  onCustomChange,
  onProductsLoaded,
  onCartsLoaded,
}) => {
  const [products, setProducts]                     = useState<BulkProduct[]>([]);
  const [carts, setCarts]                           = useState<BulkCart[]>([]);
  const [isLoadingProducts, setIsLoadingProducts]   = useState(false);
  const [isLoadingCarts, setIsLoadingCarts]         = useState(false);
  const [cartPage, setCartPage]                     = useState(0);

  // Filters
  const [tierFilter, setTierFilter]       = useState("All Tiers");
  const [riceFilter, setRiceFilter]       = useState("All");
  const [cuisineFilter, setCuisineFilter] = useState("All");

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Custom modal
  const [showCustomModal, setShowCustomModal]           = useState(false);
  const [selectedCustomItem, setSelectedCustomItem]     = useState<MenuItem | undefined>(undefined);
  const [customizedCustomItem, setCustomizedCustomItem] = useState<any>(undefined);
  const [modalOrderItems, setModalOrderItems]           = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeCustomProductId, setActiveCustomProductId] = useState<string>("");
  const [singleMenuItemsList, setSingleMenuItemsList]   = useState<MenuItem[]>([]);

  const activeCustomProductIdRef = useRef<string>("");
  const customDataRef            = useRef<BulkCustomData>(customData);

  const restaurantIdFromStore = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id,
  );
  const effectiveRestaurantId = restaurantId || restaurantIdFromStore;

  const [triggerMenuItemsList] = useLazyGetMenuItemsListQuery();
  const [triggerCarts]         = useLazyGetCartsForRestaurantByRestaurantIdQuery();
  const [menuItemTrigger]      = useLazyGetMenuItemsByRestaurantIdQuery();

  // ─── Load products ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!effectiveRestaurantId) return;
    const fetchProducts = async () => {
      try {
        setIsLoadingProducts(true);
        const [menuListRes, fullMenuRes] = await Promise.all([
          triggerMenuItemsList({ restaurantId: effectiveRestaurantId, menuType: "All" }).unwrap(),
          menuItemTrigger(effectiveRestaurantId).unwrap(),
        ]);

        const normalize = (res: any) => {
          if (!Array.isArray(res)) return [];
          if (res.length > 0 && !res[0]?.items) return res;
          return res.flatMap((cat: any) => cat.items || []);
        };

        const fullFlat: any[] = normalize(fullMenuRes);
        const listFlat: any[] = normalize(menuListRes);

        setSingleMenuItemsList(fullFlat.map((it) => ({ ...it, quantity: 0 })));

        const filtered = listFlat.filter(
          (item: any) =>
            item.itemType === "SetMenu" ||
            item.itemType === "CustomMenu" ||
            (item.itemType === "MenuItem" && item.isCustomMenuItem !== true),
        );

        const mapped: BulkProduct[] = filtered.map((item: any) => {
          const rawItem =
            fullFlat.find((f: any) => f.id === item.id || f.itemId === item.itemId) || item;
          return {
            itemId:      item.id || item.itemId,
            displayName: item.displayName,
            skuId:       item.skuId ?? "",
            tier:        getItemTier(item),
            price:       item.price || 0,
            itemType:    item.itemType,
            tinyId:      item.tinyId,
            productId:   item.productId || item.id || item.itemId,
            _rawItem:    rawItem,
          };
        });

        const tierOrder = ["Platinum", "Gold", "Silver", "Bronze", "Value", "Staff"];
        mapped.sort((a, b) => {
          const ta = tierOrder.indexOf(a.tier);
          const tb = tierOrder.indexOf(b.tier);
          if (ta !== tb) return (ta === -1 ? 99 : ta) - (tb === -1 ? 99 : tb);
          return a.displayName.localeCompare(b.displayName);
        });

        setProducts(mapped);
        onProductsLoaded?.(mapped);
      } catch (err) {
        console.error("Error loading products:", err);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRestaurantId]);

  // ─── Load carts ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!effectiveRestaurantId) return;
    setIsLoadingCarts(true);
    triggerCarts(effectiveRestaurantId)
      .unwrap()
      .then((data: any) => {
        const filtered = data.filter(
          (c: any) =>
            c.cartType === "Fixed"  || c.cartType === "fixed" ||
            c.cartType === "Moving" || c.cartType === "moving",
        );
        const mapped: BulkCart[] = filtered.map((c: any) => ({
          cartId:            c.id,
          displayName:       c.displayName,
          supportedChannels: c.supportedChannels || [],
          cartType:          c.cartType,
          cartState:         c.cartState,
        }));
        setCarts(mapped);
        onCartsLoaded?.(mapped);
      })
      .catch(console.error)
      .finally(() => setIsLoadingCarts(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveRestaurantId]);

  // ─── Filtered products ───────────────────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    let result = products;

    if (tierFilter !== "All Tiers") {
      result = result.filter((p) => p.tier === tierFilter);
    }
    if (riceFilter !== "All") {
      result = result.filter((p) => getRiceType(p) === riceFilter);
    }
    if (cuisineFilter !== "All") {
      result = result.filter((p) => getCuisineTypes(p).includes(cuisineFilter));
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (p) =>
          p.displayName.toLowerCase().includes(q) ||
          (p.skuId     && p.skuId.toLowerCase().includes(q))     ||
          (p.productId && p.productId.toLowerCase().includes(q)) ||
          (p.tinyId    && String(p.tinyId).includes(q))          ||
          p.tier.toLowerCase().includes(q)                        ||
          p.itemType.toLowerCase().includes(q),
      );
    }

    return result;
  }, [products, tierFilter, riceFilter, cuisineFilter, searchQuery]);

  // ─── Pagination ───────────────────────────────────────────────────────────────
  const totalPages   = Math.ceil(carts.length / CARTS_PER_PAGE);
  const visibleCarts = carts.slice(cartPage * CARTS_PER_PAGE, (cartPage + 1) * CARTS_PER_PAGE);

  // ─── Grid helpers ─────────────────────────────────────────────────────────────
  const handleQtyChange = (cartId: string, productId: string, value: number) =>
    onGridChange({ ...gridData, [cartId]: { ...(gridData[cartId] || {}), [productId]: value } });

  const getQty      = (cartId: string, productId: string) => gridData[cartId]?.[productId] || 0;
  const getRowTotal = (productId: string) =>
    carts.reduce((sum, c) => sum + (gridData[c.cartId]?.[productId] || 0), 0);
  const getColTotal = (cartId: string) =>
    filteredProducts.reduce((sum, p) => sum + (gridData[cartId]?.[p.itemId] || 0), 0);
  const grandTotal  = filteredProducts.reduce((sum, p) => sum + getRowTotal(p.itemId), 0);

  // ─── Custom modal helpers ─────────────────────────────────────────────────────
  const openCustomModal = (product: BulkProduct) => {
    customDataRef.current = customData;
    const productId = product.itemId;
    activeCustomProductIdRef.current = productId;
    setActiveCustomProductId(productId);
    setShowCustomModal(false);

    const raw      = product._rawItem || product;
    const existing = customDataRef.current[productId];
    const existingCustomItems: any[]      = existing?.customItems || [];
    const existingCustomItemTotal: number = existing?.customItemTotal ?? product.price ?? 0;

    const restoredCollectedItems = existingCustomItems.map((ci: any) => {
      const lookupId = ci.menuItemId || ci.itemId;
      const found    = singleMenuItemsList.find((m: any) => (m.id || m.itemId) === lookupId);
      if (found) {
        return {
          ...found,
          itemType: ci.isAddOn ? "GROUP_EXTRA_ITEM" : "SECTION_ITEM",
          isAddOn: ci.isAddOn || false,
          customMenuItemSection: found.customMenuItemSection,
          quantity: ci.quantity ?? 1,
        };
      }
      return {
        id: lookupId, displayName: ci.displayName, price: ci.unitPrice ?? 0,
        itemType: ci.isAddOn ? "GROUP_EXTRA_ITEM" : "SECTION_ITEM",
        isAddOn: ci.isAddOn || false,
        customMenuItemSection: ci.customMenuItemSection || "",
        quantity: ci.quantity ?? 1,
      };
    });

    setSelectedCustomItem(undefined);
    setCustomizedCustomItem(undefined);
    setModalOrderItems([]);

    setSelectedCustomItem({ ...raw, id: raw.id || raw.itemId || productId, quantity: 0 } as MenuItem);
    setCustomizedCustomItem({
      availableDays: raw.availableDays, priceType: raw.priceType,
      sections: raw.sections || [], totalItems: raw.totalItems || 0,
      isAvailable: raw.isAvailable,
      id: productId, itemId: productId,
      displayName: product.displayName, price: product.price,
      collectedItems: restoredCollectedItems,
    });
    setModalOrderItems(
      existingCustomItems.length > 0
        ? [{ id: productId, itemId: productId, customItems: existingCustomItems, customItemTotal: existingCustomItemTotal }]
        : [],
    );
    setShowCustomModal(true);
  };

  const interceptSetOrderItems = (updaterOrValue: any) => {
    customDataRef.current = customData;
    setModalOrderItems((prev: any[]) => {
      const next      = typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
      const currentId = activeCustomProductIdRef.current;
      const produced  = next.find((oi: any) => oi.id === currentId || oi.itemId === currentId);
      if (produced) {
        customDataRef.current = {
          ...customDataRef.current,
          [currentId]: {
            customItems:     produced.customItems || [],
            customItemTotal: produced.customItemTotal ?? produced.calculatedPrice ?? produced.price ?? 0,
          },
        };
        onCustomChange(customDataRef.current);
      }
      return next;
    });
  };

  useEffect(() => { customDataRef.current = customData; }, [customData]);

  const hasActiveFilters =
    tierFilter !== "All Tiers" || riceFilter !== "All" || cuisineFilter !== "All" || !!searchQuery;

  const clearAllFilters = () => {
    setTierFilter("All Tiers");
    setRiceFilter("All");
    setCuisineFilter("All");
    setSearchQuery("");
  };

  // ─── Loading ──────────────────────────────────────────────────────────────────
  if (isLoadingProducts || isLoadingCarts) {
    return (
      <div className="bulk-grid-loading">
        <div className="bulk-grid-spinner" />
        <div>Loading {isLoadingProducts ? "products" : "carts"}…</div>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="bulk-grid-container">

      {/* ── Filter Bar ──────────────────────────────────────────────────────── */}
      <div className="bulk-filter-bar">

        {/* TIER */}
        <div className="bulk-filter-group">
          <span className="bulk-filter-label">TIER</span>
          <div className="bulk-filter-chips">
            {TIER_FILTERS.map((f) => (
              <button
                key={f}
                className={`bulk-chip ${tierFilter === f ? "bulk-chip-active" : ""}`}
                onClick={() => setTierFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="bulk-filter-divider" />

        {/* RICE */}
        <div className="bulk-filter-group">
          <span className="bulk-filter-label">RICE</span>
          <div className="bulk-filter-chips">
            {RICE_FILTERS.map((f) => (
              <button
                key={f}
                className={`bulk-chip ${riceFilter === f ? "bulk-chip-active" : ""}`}
                onClick={() => setRiceFilter(f)}
              >
                {f === "All" ? "All Rice" : `${f} Rice`}
              </button>
            ))}
          </div>
        </div>

        <div className="bulk-filter-divider" />

        {/* CUISINE */}
        <div className="bulk-filter-group">
          <span className="bulk-filter-label">CUISINE</span>
          <div className="bulk-filter-chips">
            {CUISINE_FILTERS.map((f) => (
              <button
                key={f}
                className={`bulk-chip ${cuisineFilter === f ? "bulk-chip-active" : ""}`}
                onClick={() => setCuisineFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Clear all */}
        {hasActiveFilters && (
          <>
            <div className="bulk-filter-divider" />
            <button className="bulk-filter-clear-all" onClick={clearAllFilters}>
              <FontAwesomeIcon icon={faTimes} />
              Clear
            </button>
          </>
        )}
      </div>

      {/* ── Stats + Search ──────────────────────────────────────────────────── */}
      <div className="bulk-grid-stats">
        <div className="bulk-stat">
          <span className="bulk-stat-label">Products</span>
          <span className="bulk-stat-value">
            {hasActiveFilters ? (
              <>
                <span className="bulk-stat-filtered">{filteredProducts.length}</span>
                <span className="bulk-stat-of"> / {products.length}</span>
              </>
            ) : (
              products.length
            )}
          </span>
        </div>
        <div className="bulk-stat">
          <span className="bulk-stat-label">Carts</span>
          <span className="bulk-stat-value">{carts.length}</span>
        </div>
        <div className="bulk-stat">
          <span className="bulk-stat-label">Total Qty</span>
          <span className="bulk-stat-value bulk-stat-highlight">{grandTotal}</span>
        </div>

        {/* Search */}
        <div className="bulk-search-wrapper">
          <FontAwesomeIcon icon={faSearch} className="bulk-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            className="bulk-search-input"
            placeholder="Search by name, SKU, product ID…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="bulk-search-clear"
              onClick={() => { setSearchQuery(""); searchInputRef.current?.focus(); }}
              title="Clear search"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bulk-grid-pagination">
            <button
              className="bulk-page-btn"
              disabled={cartPage === 0}
              onClick={() => setCartPage((p) => p - 1)}
            >
              ← Prev
            </button>
            <span className="bulk-page-label">
              Carts {cartPage * CARTS_PER_PAGE + 1}–
              {Math.min((cartPage + 1) * CARTS_PER_PAGE, carts.length)} of {carts.length}
            </span>
            <button
              className="bulk-page-btn"
              disabled={cartPage >= totalPages - 1}
              onClick={() => setCartPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* ── Grid Table ──────────────────────────────────────────────────────── */}
      <div className="bulk-grid-wrap">
        <table className="bulk-table">
          <thead>
            <tr>
              <th className="bulk-th-product">PRODUCT / SKU</th>
              {visibleCarts.map((cart, i) => (
                <th key={cart.cartId} className="bulk-th-cart">
                  <span className="bulk-cart-num">Cart {cartPage * CARTS_PER_PAGE + i + 1}</span>
                  <span className="bulk-cart-name" title={cart.displayName}>
                    {cart.displayName}
                  </span>
                </th>
              ))}
              <th className="bulk-th-customize">CUSTOMIZE</th>
              <th className="bulk-th-total">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan={visibleCarts.length + 3} className="bulk-td-empty">
                  <div className="bulk-empty-state">
                    <FontAwesomeIcon icon={faSearch} className="bulk-empty-icon" />
                    <div className="bulk-empty-title">No products found</div>
                    <div className="bulk-empty-sub">
                      Try adjusting the filters or search term.
                    </div>
                    <button className="bulk-empty-clear" onClick={clearAllFilters}>
                      Clear all filters
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => {
                const tierStyle    = getTierStyle(product.tier);
                const isCustom     = product.itemType === "CustomMenu";
                const customConfig = customData[product.itemId];
                const rowTotal     = getRowTotal(product.itemId);

                return (
                  <tr key={product.itemId} className="bulk-tr">
                    {/* Product cell */}
                    <td className="bulk-td-product" style={{ background: tierStyle.bg }}>
                      <div className="bulk-product-inner">
                        <span
                          className="bulk-tier-badge"
                          style={{ background: tierStyle.tag, color: "#fff" }}
                        >
                          {getTierIcon(product.tier)} {product.tier}
                        </span>
                        <div>
                          <div className="bulk-product-name">
                            {highlightMatch(product.displayName, searchQuery)}
                          </div>
                          <div className="bulk-product-sku">
                            {product.skuId || ""}
                            {" "}
                            <span className="bulk-product-menuType">{product.itemType}</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Qty cells */}
                    {visibleCarts.map((cart) => {
                      const qty = getQty(cart.cartId, product.itemId);
                      return (
                        <td key={cart.cartId} className="bulk-td-qty">
                          <input
                            type="number"
                            min={0}
                            className={`bulk-qty-input ${qty === 0 ? "bulk-qty-zero" : "bulk-qty-filled"}`}
                            value={qty === 0 ? "" : qty}
                            placeholder="0"
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) =>
                              handleQtyChange(cart.cartId, product.itemId, parseInt(e.target.value) || 0)
                            }
                          />
                        </td>
                      );
                    })}

                    {/* Customize cell */}
                    <td className="bulk-td-customize">
                      {isCustom ? (
                        <button
                          className={`bulk-customize-btn ${
                            customConfig?.customItems?.length
                              ? "bulk-customize-btn-done"
                              : "bulk-customize-btn-todo"
                          }`}
                          onClick={() => openCustomModal(product)}
                          title="Configure custom items"
                        >
                          <FontAwesomeIcon icon={faSliders} />
                          {customConfig?.customItems?.length ? (
                            <span className="bulk-customize-count">
                              {customConfig.customItems.length}
                            </span>
                          ) : null}
                        </button>
                      ) : (
                        <span className="bulk-customize-na">—</span>
                      )}
                    </td>

                    {/* Row total */}
                    <td className="bulk-td-total">
                      <span className={rowTotal === 0 ? "bulk-total-zero" : "bulk-total-val"}>
                        {rowTotal}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot>
            <tr className="bulk-tfoot-row">
              <td className="bulk-tfoot-label">Column Totals</td>
              {visibleCarts.map((cart) => {
                const colTotal = getColTotal(cart.cartId);
                return (
                  <td key={cart.cartId} className="bulk-tfoot-qty">
                    <span className={colTotal === 0 ? "bulk-total-zero" : "bulk-tfoot-val"}>
                      {colTotal}
                    </span>
                  </td>
                );
              })}
              <td className="bulk-tfoot-customize-placeholder" />
              <td className="bulk-tfoot-grand">
                <span>{grandTotal}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Custom item modal */}
      {showCustomModal && selectedCustomItem && (
        <CustomItemSetupModal
          showModal={showCustomModal}
          handleModalClose={() => setShowCustomModal(false)}
          selectedCustomItem={selectedCustomItem}
          setSelectedCustomItem={setSelectedCustomItem}
          singleMenuItemsList={singleMenuItemsList}
          control={{}}
          customizedCustomItem={customizedCustomItem}
          setCustomizedCustomItem={setCustomizedCustomItem}
          onSaveCollectedItems={() => {}}
          orderItems={modalOrderItems}
          setOrderItems={interceptSetOrderItems}
          selectedMenuItemList={[]}
          setSelectedMenuItemList={() => {}}
        />
      )}
    </div>
  );
};

// ─── Highlight matching text ──────────────────────────────────────────────────
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const regex = new RegExp(
    `(${query.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bulk-search-highlight">{part}</mark>
        ) : (
          part
        ),
      )}
    </>
  );
}